import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import { AlertMuteButton } from '@/components/AlertMuteButton';
import { AlertUnmuteButton } from '@/components/AlertUnmuteButton';
import { AlertExcludeButton } from '@/components/AlertExcludeButton';
import { AlertRemoveExclusionButton } from '@/components/AlertRemoveExclusionButton';
import { AlertEditPolicyButton } from '@/components/AlertEditPolicyButton';
import {
  ALERT_ORGANIZATION_SCOPE_FILTER_VALUE,
  ALERT_ORGANIZATION_SCOPE_LABEL,
  ORGANIZATION_ALERTS_FILTERABLE_FIELDS,
  OrganizationScanAlertsSortField,
  outcomeOptions,
  type GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { AlertSecurityGroupsCell } from '@/components/grouped-alerts/AlertSecurityGroupsCell';
import { listSecurityGroups } from '@kirin/api-client/api/security-groups';
import { apiClient } from '@/lib/api-client';
import { ALERTS_FILTER_URL_KEYS } from './utils';
import { severityOptionsDescending } from '@kirin/api-client/contracts/shared/severity';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import {
  formatTypeFilterLabel,
  formatTitleFilterLabel,
} from '@kirin/api-client/helpers/alerts';
import {
  TypeCell,
  SeverityCell,
  FilePathCell,
  IssuesCell,
  ScoreCell,
  LastSeenCell,
  FirstSeenCell,
  ClientTypesCell,
  OutcomeCell,
  TITLE_COLUMN_WIDTH,
} from './CellRenderers';
import { UsersCell } from '@/components/grouped-alerts/UsersCell';
import { AlertEndpointsCell } from '@/components/grouped-alerts/AlertEndpointsCell';
import { getTimestampColumnWidth } from '@/components/shared/timestampColumnWidth';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import {
  FilePathFilterOption,
  SeverityFilterOption,
  UserFilterOption,
  ClientTypeFilterOption,
} from './FilterComponents';
import { getPlatformLabel } from '@/components/shared/platform/platform-utils';
import {
  fetchAlertFilterOptions,
  fetchAlertFileOptions,
  fetchMemberFilterOptions,
  getFilterOptionsQueryKey,
  getUserFilterOptionsQueryKey,
} from './api';

const ValuelessOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...ValuelessOperators,
];
/**
 * Generate column definitions for the organization alerts table.
 * Requires organizationId for dynamic filter options.
 *
 * `canFilterByUser` (owner/manager only) gates the Users column's filter:
 * its dropdown options are sourced from the org members list, an endpoint
 * RBAC-gated to owner/manager. For members the column stays visible and
 * sortable but is non-filterable, so no members fetch (→ 403) ever fires
 * (KIR-3266). Defaults to `false` — fail safe for any caller that omits it.
 */
export const createAlertsColumns = (
  organizationId: string,
  includeIdeColumn: boolean = false,
  revampEnabled: boolean = false,
  canFilterByUser: boolean = false,
  timestampFormat: TimestampFormat = 'relative',
  includeEndpointsColumn: boolean = false,
  includeSecurityGroupColumn: boolean = false,
  // KIR-3800: owner/manager only — NOT the same set as `canFilterByUser`. The
  // dropdown enumerates the org's whole group roster, which is management data
  // owned by the (manager/owner-only) Security Groups page; the column itself
  // stays ungated because attribution is alert context. See the posture comment
  // in DataTableAlertsTab/index.tsx. Defaults to false — fail safe.
  canFilterBySecurityGroup: boolean = false
): ColumnConfig<GroupedAlert>[] => {
  const timestampColumnWidth = getTimestampColumnWidth(timestampFormat);
  // Users column: filterable (member-backed dropdown) for owner/manager,
  // otherwise a sortable-but-non-filterable column with no member fetch.
  const usersColumn: ColumnConfig<GroupedAlert> = canFilterByUser
    ? {
        id: 'users',
        header: 'Users',
        accessor: 'users',
        renderCell: ({ row }) => <UsersCell alert={row} />,
        width: 120,
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getUserFilterOptionsQueryKey(organizationId, params),
          queryFn: params => fetchMemberFilterOptions(organizationId, params),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          render: (option, isSelected) => (
            <UserFilterOption
              userId={option.value}
              userName={option.metadata?.name as string | null}
              userEmail={option.metadata?.email as string | null}
              isSelected={isSelected}
            />
          ),
        },
      }
    : {
        id: 'users',
        header: 'Users',
        accessor: 'users',
        renderCell: ({ row }) => <UsersCell alert={row} />,
        width: 120,
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: false,
      };

  /*
    KIR-3800: Security Group column, gated by `security_groups_policies`.

    Fully server-side, like every other column here: the group set arrives on
    the alert (`securityGroups`), `sortField` names a real backend ORDER BY
    (MIN(security_groups.name), ungrouped collating under "Organization"), and the filter is a real
    `ORGANIZATION_ALERTS_FILTERABLE_FIELDS` entry matching any occurrence's
    group. Nothing about this column is derived from the current page.

    The filter's options come from the org's security-groups endpoint rather
    than a dedicated alerts filter-options endpoint: the group list is small and
    admin-curated, so a second source of truth would earn nothing.

    It requests `includeDeleted`. Alerts keep naming the group that triggered
    them after deletion, so options sourced from live groups only would leave a
    group visible in the rows but unselectable in the filter.
  */
  const securityGroupColumnBase = {
    id: 'securityGroups',
    header: 'Security Group',
    accessor: 'securityGroups' as const,
    renderCell: ({ row }: { row: GroupedAlert }) => (
      <AlertSecurityGroupsCell alert={row} />
    ),
    width: 150,
    sortable: true,
    sortField: OrganizationScanAlertsSortField.SecurityGroup,
    sortKey: 'sg',
  };

  /*
    The column stays visible and sortable for everyone: the group arrives on the
    alert itself and the sort is server-side on the alerts endpoint, neither of
    which needs elevated access.

    Only the FILTER is gated, and to owner/manager — a narrower set than the
    Users filter above. Two separate reasons, both load-bearing:

      1. Its options come from GET /security-groups. A plain member (the Alerts
         tab is anyRole) would fire that fetch, take a 403, and be left with a
         permanently empty dropdown — the KIR-3266 failure the Users column
         already guards against.
      2. The dropdown lists every group in the org, deleted ones included. That
         is the roster the Security Groups page owns, and that page is
         deliberately manager/owner. Support may read a group NAME off an alert
         it is triaging, but enumerating the roster is management. The endpoint
         would happily serve support (it is manager/owner/support); we choose
         not to ask.
  */
  const securityGroupColumn: ColumnConfig<GroupedAlert> =
    canFilterBySecurityGroup
      ? {
          ...securityGroupColumnBase,
          filterable: true,
          dataType: ColumnDataTypes.ENUM,
          // Deliberately NO valueless operators. "Is Empty"/"Is Not Empty" compile
          // to `expr IS NULL` / `IS NOT NULL`, and this column's HAVING expression
          // is COALESCE(group_id, '<organization sentinel>') — never null. They
          // would be silently dead controls. The "Organization" option is the
          // correct way to express "ungrouped", and it filters properly.
          filterOperators: [
            { value: FilterOperators.INCLUDES, label: 'Includes' },
            { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ],
          statePersistence: {
            urlKey:
              ALERTS_FILTER_URL_KEYS[
                ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SECURITY_GROUPS
              ],
          },
          filterOptions: {
            type: FilterOptionTypes.DYNAMIC,
            // Distinct from `queryKeys.securityGroups.list`: that cache holds LIVE
            // groups only, and reusing its key here would serve the alerts filter
            // a list with the deleted groups missing (and vice versa).
            getQueryKey: () => [
              'securityGroups',
              organizationId,
              'includeDeleted',
            ],
            queryFn: async () => {
              // `includeDeleted`: alerts keep naming the group that triggered them
              // after it is deleted, so a filter sourced from live groups only
              // could not match rows that are visibly on screen. Deleted groups
              // are labelled so picking one is an informed choice.
              const groups = await listSecurityGroups(
                apiClient,
                organizationId,
                {
                  includeDeleted: true,
                }
              );
              return {
                options: [
                  // "Organization" is a real, selectable value: server-side the
                  // filter COALESCEs an ungrouped finding's NULL group to this
                  // sentinel, so it includes/excludes like any group. Listed first
                  // because it is the default scope for every ungrouped user.
                  {
                    value: ALERT_ORGANIZATION_SCOPE_FILTER_VALUE,
                    label: ALERT_ORGANIZATION_SCOPE_LABEL,
                  },
                  ...groups.map(group => ({
                    value: group.id,
                    label: group.deletedAt
                      ? `${group.name} (deleted)`
                      : group.name,
                  })),
                ],
                hasMore: false,
              };
            },
            queryConfig: {
              staleTime: 2 * 60 * 1000,
            },
            /*
            The dropdown's type-ahead narrows the fetched options locally instead
            of forwarding a `search` param.

            Required here, not just an optimisation: this source returns the
            org's whole (small, admin-curated) group list in one unpaginated call
            and has no server-side search to forward to. Left server-side, the
            typed term would go to a query that ignores it — and be stripped from
            the query key — so the list would never narrow.

            It also matches on the LABEL, which is what the user sees and types:
            "Organization", and a deleted group's "Retired (deleted)". The raw
            option value is a uuid and would match nothing.
          */
            clientSideSearch: true,
          },
        }
      : { ...securityGroupColumnBase, filterable: false };

  // KIR-3035: Endpoints column — single endpoint shows its name, multiple show
  // the count; click opens the "Affected Endpoints" popover + details dialog.
  const endpointsColumn: ColumnConfig<GroupedAlert> = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
    header: 'Endpoints',
    accessor: 'endpoints',
    renderCell: ({ row }) => <AlertEndpointsCell alert={row} />,
    width: 160,
    // KIR-3035: sort by the number of distinct endpoints an alert was seen on.
    sortable: true,
    sortField: 'endpointCount',
    sortKey: 'ep',
    // Filter by endpoint name (multi-select of distinct names on this alert set).
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getFilterOptionsQueryKey(
          organizationId,
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
          params
        ),
      queryFn: params =>
        fetchAlertFilterOptions(
          organizationId,
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
          params
        ),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
    },
  };

  const clientTypesColumn: ColumnConfig<GroupedAlert> = {
    id: 'clientTypes',
    header: 'IDE/Platforms',
    accessor: 'clientTypes',
    renderCell: ({ row }) => <ClientTypesCell row={row} />,
    width: 200,
    sortable: true,
    sortField: 'clientTypes',
    sortKey: 'ct',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getFilterOptionsQueryKey(organizationId, 'clientTypes', params),
      queryFn: params =>
        fetchAlertFilterOptions(organizationId, 'clientTypes', params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: getPlatformLabel,
      render: (option, isSelected) => (
        <ClientTypeFilterOption
          clientType={option.value}
          isSelected={isSelected}
        />
      ),
    },
  };

  // KIR-3769: the Title/Type column.
  // - revamp ON: a composite "Title" filter (field `title`, value
  //   `<subPolicyKey>::<type>`) so two titles sharing one `type` slug become
  //   separate, individually-filterable options, and the option label matches
  //   the rendered cell. Type-ahead is client-side (the composite key is not
  //   the label). Uses its own `ti` url key; the legacy `t` (type) deep links
  //   are kept working by the hidden back-compat `type` column below.
  // - revamp OFF: byte-identical to the pre-KIR-3769 Type column (field
  //   `type`, server-side search, `t` url key).
  const titleOrTypeColumn: ColumnConfig<GroupedAlert> = revampEnabled
    ? {
        id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
        header: 'Title',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        // Sort still uses the `type` slug server-side (unchanged); only the
        // filter identity changed. Keep the `t` sort key so sort deep links
        // are unaffected.
        sortField: 'type',
        sortKey: 't',
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getFilterOptionsQueryKey(
              organizationId,
              ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
              params
            ),
          queryFn: params =>
            fetchAlertFilterOptions(
              organizationId,
              ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
              params
            ),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          // Composite key values aren't human-readable; search the labels
          // locally so typing a displayed title works.
          clientSideSearch: true,
          labelFormatter: formatTitleFilterLabel,
          // The active-filter tooltip resolves its text from the FETCHED
          // options and only falls back to this formatter on a miss. A
          // deep-linked value can legitimately be missing (options not yet
          // resolved, confidence cutoff, the 200-option limit, or label
          // dedupe), and without this the tooltip shows the raw composite.
          tooltip: { labelFormatter: formatTitleFilterLabel },
        },
      }
    : {
        id: 'type',
        header: 'Type',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        // KIR-2941: shared with the personal alerts table via the constant.
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        sortField: 'type',
        sortKey: 't',
        filterable: true,
        dataType: ColumnDataTypes.STRING,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getFilterOptionsQueryKey(organizationId, 'type', params),
          queryFn: params =>
            fetchAlertFilterOptions(organizationId, 'type', params),
          queryConfig: {
            staleTime: 2 * 60 * 1000, // 5 minutes
          },
          // KIR-2381: drives dropdown labels off the same registry as the
          // cell renderer so option labels match what users see in the table.
          labelFormatter: (type: string) =>
            formatTypeFilterLabel(type, revampEnabled),
          // Same reason as the Title column: the tooltip falls back to this
          // when a deep-linked value is absent from the fetched options.
          tooltip: {
            labelFormatter: (type: string) =>
              formatTypeFilterLabel(type, revampEnabled),
          },
        },
      };

  // KIR-3769 back-compat: revamp-on the visible column filters on `title`
  // (url key `ti`), so a legacy "View Details" deep link that still targets the
  // `type` filter (url key `t`, emitted by the Go agent + VS Code extension —
  // KIR-2659) would otherwise be silently dropped by the DataTable's
  // url-key→field map. This hidden column keeps the `type` filter field alive
  // (same `t` url key + server-side type options) WITHOUT rendering a second
  // visible header/cell, so those deep links resolve exactly as before. It is
  // only added when the composite title column has taken over the `t` slug.
  const hiddenTypeCompatColumn: ColumnConfig<GroupedAlert> = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE,
    header: 'Type',
    accessor: 'type',
    hidden: true,
    filterable: true,
    dataType: ColumnDataTypes.STRING,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getFilterOptionsQueryKey(organizationId, 'type', params),
      queryFn: params =>
        fetchAlertFilterOptions(organizationId, 'type', params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: (type: string) =>
        formatTypeFilterLabel(type, revampEnabled),
    },
  };

  return [
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      // Widened so the absolute UTC string fits without truncation when the
      // Timestamp Format setting is "absolute" (KIR-1285).
      width: timestampColumnWidth,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    usersColumn,
    ...(includeSecurityGroupColumn ? [securityGroupColumn] : []),
    ...(includeEndpointsColumn ? [endpointsColumn] : []),
    titleOrTypeColumn,
    ...(revampEnabled ? [hiddenTypeCompatColumn] : []),
    {
      id: 'severity',
      header: 'Severity',
      accessor: 'severity',
      renderCell: ({ row }) => <SeverityCell row={row} />,
      width: 120,
      sortable: true,
      sortField: 'severity',
      sortKey: 'sev',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: severityOptionsDescending,
        render: (option, isSelected) => (
          <SeverityFilterOption
            severity={option.value}
            isSelected={isSelected}
          />
        ),
        tooltip: {
          labelFormatter: getSeverityLabel,
        },
      },
    },
    {
      id: 'outcome',
      header: 'Outcome',
      accessor: 'outcome',
      renderCell: ({ row }) => <OutcomeCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'outcome',
      sortKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME],
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: outcomeOptions.map(o => ({ value: o.value, label: o.label })),
      },
    },
    {
      id: 'filePath',
      header: 'File',
      accessor: 'filePath',
      renderCell: ({ row }) => <FilePathCell row={row} />,
      width: 320,
      // File column is display-only: the cell shows a resolved label
      // (real path from `location`, or a friendly sentinel description),
      // which server-side sort on the stored `filePath` cannot match. KIR-3154.
      sortable: false,
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getFilterOptionsQueryKey(organizationId, 'filePath', {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params => fetchAlertFileOptions(organizationId, params),
        queryConfig: {
          staleTime: 2 * 60 * 1000,
        },
        dropdown: {
          width: 520,
        },
        pagination: {
          mode: 'offset',
          limit: 5,
        },
        render: (option, isSelected) => (
          <FilePathFilterOption
            filePath={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    ...(includeIdeColumn ? [clientTypesColumn] : []),
    {
      id: 'occurrenceCount',
      header: 'Issues',
      accessor: 'occurrenceCount',
      renderCell: ({ row }) => <IssuesCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'occurrenceCount',
      sortKey: 'oc',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT
          ],
      },
      range: {
        min: 1,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'score',
      header: 'Score',
      accessor: 'score',
      renderCell: ({ row }) => <ScoreCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'score',
      sortKey: 'sc',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SCORE],
      },
      range: {
        min: 0,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First Seen',
      accessor: 'firstSeenAt',
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      // Widened so the absolute UTC string fits without truncation when the
      // Timestamp Format setting is "absolute" (KIR-1285).
      width: timestampColumnWidth,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
};

/**
 * Factory for the Actions column that renders mute/unmute and
 * exclude/remove-exclusion controls per row.
 *
 * The column is consumed as an additive append to the columns array only
 * when EITHER `alert_muting` or `alert_ignore` is on AND the viewer is
 * owner / manager, so flag-off and member views keep the exact pre-KIR-843
 * layout. Each individual button is itself flag-gated (renders `null` when
 * its flag is off or the role check fails), so the cell composes naturally
 * across the matrix of feature-flag states without per-button orchestration
 * here.
 */
export function createOrganizationAlertsActionsColumn(
  organizationId: string
): ColumnConfig<GroupedAlert> {
  return {
    id: 'actions',
    header: 'Actions',
    // 100 (Mute/Unmute) + 168 (Exclude/Remove) + 100 (Edit Policy) + 16
    // (gap-2 x2) + 24 (TableCell px-3) + 8 (pr-2 trailing) = 416. Keep in
    // lock-step with MUTE_BUTTON_WIDTH_CLASS / EXCLUDE_BUTTON_WIDTH_CLASS /
    // EDIT_POLICY_BUTTON_WIDTH_CLASS and the cell wrapper gap/padding below.
    width: 416,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const scope = {
        kind: 'organization' as const,
        organizationId,
      };
      const analyticsContext = { severity: row.severity, type: row.type };
      // Suppress click-through to row expansion. Buttons are interactive and
      // own their own focus semantics; clicking a button should not also
      // toggle the row.
      return (
        <div
          className="flex items-center justify-start gap-2 pr-2"
          onClick={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          {row.mute?.muted ? (
            <AlertUnmuteButton
              scope={scope}
              alertKey={row.alertKey}
              mute={row.mute}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertMuteButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
            />
          )}
          {row.exclude?.excluded ? (
            <AlertRemoveExclusionButton
              scope={scope}
              alertKey={row.alertKey}
              exclude={row.exclude}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertExcludeButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
              findingType={row.type}
            />
          )}
          <AlertEditPolicyButton
            scope={{ kind: 'organization', organizationId }}
            policyKey={row.policyKey}
            subPolicyKey={row.subPolicyKey}
            alertKey={row.alertKey}
          />
        </div>
      );
    },
  };
}
